import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';

import useUpSellsCarouselQuery from 'api/queries/useUpSellsCarouselQuery';
import ProductCardV2 from 'features/shop/ProductCardV2';
import { ProductCardProvider } from 'features/shop/ProductCardV2/ProductCardProvider';
import CarouselBase, { SlideBase } from 'honeydew/Carousels/shared/CarouselBase';
import useGlobalStore from 'hooks/useGlobalStore';
import TrackInView from 'shared/TrackInView';

/**
 * CartUpsells component displays a carousel of upsell products in the cart.
 *
 * @param {Object} props - Component props
 * @param {string} [props.name=''] - Custom name for the carousel
 * @param {Function} [props.setUpsellsVisible=() => {}] - Callback to set upsells visibility
 * @param {string} [props.slideSize='80%'] - Size of each slide
 */
function CartUpsells({ className = 'py-10 bg-lime', name = '', setUpsellsVisible = () => {}, slideSize = '80%' }) {
    const navigate = useNavigate();
    const { setCartOpen } = useGlobalStore();

    const upsellsCarouselQuery = useUpSellsCarouselQuery({
        position: 'cart',
        sellableItemID: null,
    });
    const { data: carousel } = upsellsCarouselQuery;
    const { dsSource, items } = carousel?.results ?? {};

    const canShowCarousel = items?.length > 0;
    const [showCarousel, setShowCarousel] = useState(canShowCarousel);

    useEffect(() => {
        setShowCarousel(canShowCarousel);
    }, [canShowCarousel]);

    const navigateToPDP = ({ productId }) => {
        if (window.isNativeApp) {
            window.ReactNativeWebView?.postMessage(JSON.stringify({ navigate: 'PDP', pdpId: productId }));
        } else {
            setCartOpen(false);
            navigate(`/shop/product/${productId}`);
        }
    };

    useEffect(() => {
        setUpsellsVisible(showCarousel);
    }, [showCarousel, setUpsellsVisible]);

    const carouselData = {
        id: 'cart_upsells',
        name: name || 'Suggested for you',
        items,
        featured: false,
    };

    const shouldShowArrows = items?.length > 1;

    const carouselSlides = useMemo(
        () =>
            items?.map((item) => (
                <SlideBase key={item.id} slideSize={slideSize} className="bg-white p-10">
                    <ProductCardV2
                        data={item}
                        dsSourceOverride={dsSource}
                        layout="horizontal"
                        metaData={{
                            isCartUpsell: true,
                            position: 'cart',
                            upSellsSource: 'cart_upsell',
                        }}
                        onNavigateToPDP={() => setCartOpen(false)}
                    />
                </SlideBase>
            )),
        [items, dsSource, slideSize, navigateToPDP]
    );

    if (!showCarousel || !carouselSlides?.length) return null;

    return (
        <TrackInView
            className={className}
            data-testid="upsells-cart"
            eventName="carousel view"
            eventProperties={{
                carousel_name: 'Cart Upsell',
            }}
        >
            <ProductCardProvider>
                <CarouselBase
                    arrows={shouldShowArrows}
                    data={carouselData}
                    slideGap="gap-10"
                    styles={{ itemsViewport: 'px-10', header: 'px-10' }}
                >
                    {carouselSlides}
                </CarouselBase>
            </ProductCardProvider>
        </TrackInView>
    );
}

export default CartUpsells;
